// Regimens registry (PLAN.md §6.1.1, D-4) + the Bedrock tool for group capture
// (§17.11). Pure data — safe for both API and web.

import { isFormUnit, type MedFormUnit, type MedIngredient, type MedRoute, type MedSchedule, type MedUnit, type ScheduledGroup } from "./medication.js";

export interface PrnFollowUp {
  enabled: boolean;
  delayMinutes: number; // 15 | 30 | 45 | 60 | 90 | 120 | 1440 (D-5)
}

export interface RegimenItem {
  lineId: string;
  name: string;
  dose?: number; // set for single-ingredient meds; omitted when `ingredients` is set
  unit?: MedUnit; // set for single-ingredient meds; omitted when `ingredients` is set
  ingredients?: MedIngredient[]; // combination product breakdown (D-6)

  /**
   * Amount of `unit` contained in one physical `pillForm` unit (e.g. 200 for
   * 200 mg tablets). Only meaningful when `unit` is a continuous measure
   * (mg/mcg/g/IU/unit/mEq/mL/tsp/tbsp/%) — when `unit` is already a form unit
   * (tablet/capsule/...) the dose IS the pill count already, so this is unused.
   * Absent ⇒ the pill-count stepper is unavailable for this item and dose
   * entry falls back to the existing free-numeric-mg field
   * (MEDICATION_PILL_STEPPER_PLAN.md).
   */
  pillStrength?: number;
  /** Display label for one pill when `pillStrength` is set. Defaults to "tablet". */
  pillForm?: MedFormUnit;
  /** When true, the pill-count stepper increments by 0.5 instead of 1. Default/absent = whole pills only. */
  pillHalfSteps?: boolean;
  /** When true, the pill-count stepper increments by 0.25 (a superset of half steps; wins over `pillHalfSteps`). */
  pillQuarterSteps?: boolean;

  route?: MedRoute; // default oral
  intendedFor?: string;
  instructions?: string;
  prescribingProvider?: string;
  prnFollowUp?: PrnFollowUp; // only used when this med is logged as PRN

  /**
   * Optional, user-set dosing schedule (MEDICATION_SCHEDULE_PLAN.md). When
   * present, the proactive-suggestion engine schedules this medication
   * item-by-item from the schedule (every-N-days relative to last dose,
   * days-of-week, every-N-hours, or specific times) instead of the regimen's
   * day-part anchor — and works even for as_needed_inventory items. Absent ⇒
   * existing behavior (regimen day-part anchor, learned, or never-suggested).
   */
  schedule?: MedSchedule;

  /**
   * Whether this medication is currently active. Absent ⇒ active (no migration —
   * every existing item stays active). When `false` the med is "paused": excluded
   * from proactive suggestions, interaction checks, and the chat assistant's
   * context (MEDICATION_INACTIVE_PLAN.md D-B/D-D), but its logged doses are
   * preserved. Logging a specific dose of it reactivates it (D-C).
   */
  active?: boolean;

  /**
   * Hard floor between doses (hours). When set, the proactive-suggestion engine
   * skips this medication if the last logged dose is more recent than this value —
   * regardless of window math, anchor logic, or guard results. Useful for once-daily
   * medications (e.g. Claritin D 24-hour → 20, Ambien → 8) where re-suggestion
   * within the interval is never appropriate.
   */
  minHoursBetweenDoses?: number;

  // ── Canonical drug identity (resolved server-side from `name` via RxNav) ──
  // We persist the stable RxNorm concept id rather than denormalizing brand /
  // generic display names onto the (high-volume, immutable) medication_taken
  // events. The id is the join key into the global DrugReference cache for
  // synonyms in reports, and lets the interaction check skip name re-resolution.
  /** RxNorm concept id for a single-ingredient product. Absent for combination
   *  products (each ingredient carries its own rxcui) and for unresolved names. */
  rxcui?: string;
  /** RxNav's canonical name for `rxcui` (e.g. "ibuprofen"); display/debug aid. */
  rxName?: string;
  /** When resolution last ran. Set even on a failed lookup so we don't re-hammer
   *  RxNav every save, and re-resolve only when `name` changes. */
  rxResolvedAt?: string;
}

export interface Regimen {
  regimenId: string;
  patientId: string;
  name: string; // e.g. "Morning Pills"
  kind: "scheduled" | "as_needed_inventory";
  scheduledGroup?: ScheduledGroup; // for scheduled kind: morning/night/noon/other
  defaultTime?: string; // "HH:MM" local hint for time inference
  items: RegimenItem[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── Pill-count stepper helpers (MEDICATION_PILL_STEPPER_PLAN.md) ──────────────

/** True when this item has a usable pill-count stepper configured. */
export function hasPillStepper(item: Pick<RegimenItem, "unit" | "pillStrength">): boolean {
  return typeof item.pillStrength === "number" && item.pillStrength > 0 && !isFormUnit(item.unit);
}

type PillStepFlags = Pick<RegimenItem, "pillHalfSteps" | "pillQuarterSteps">;

/** Stepper granularity for an item: 0.25 (quarter), 0.5 (half), or 1 (whole pills). */
export function pillStep(item: PillStepFlags): number {
  return item.pillQuarterSteps ? 0.25 : item.pillHalfSteps ? 0.5 : 1;
}

/** Round a raw pill count to the item's step granularity (0.25, 0.5 or 1), floored at one step. */
export function roundPillCount(item: PillStepFlags, raw: number): number {
  const step = pillStep(item);
  if (!Number.isFinite(raw) || raw <= 0) return step;
  return Math.max(step, Math.round(raw / step) * step);
}

/** Pill count implied by a total dose, e.g. dose=400, pillStrength=200 -> 2. */
export function pillCountForDose(
  item: Pick<RegimenItem, "pillStrength"> & PillStepFlags,
  dose: number
): number {
  if (!item.pillStrength) return pillStep(item);
  return roundPillCount(item, dose / item.pillStrength);
}

/** Total dose for a given pill count, rounded to 2 decimals to avoid float dust. */
export function doseForPillCount(item: Pick<RegimenItem, "pillStrength">, count: number): number {
  return Math.round(count * (item.pillStrength ?? 0) * 100) / 100;
}

/** "2 tablets", "1.5 capsules", "0.25 tablets" — pluralizes off count !== 1. */
export function formatPillCount(item: Pick<RegimenItem, "pillForm">, count: number): string {
  const form = item.pillForm ?? "tablet";
  const label = count === 1 ? form : `${form}s`;
  // Quarter steps need two decimals (1.25); String() keeps whole/half counts as "2" / "1.5".
  const numStr = String(Math.round(count * 100) / 100);
  return `${numStr} ${label}`;
}

// ── Active/inactive helpers (MEDICATION_INACTIVE_PLAN.md D-B) ─────────────────
// A medication is "inactive" only when its item carries `active === false`. An
// absent flag means active, so legacy items (and every item created before this
// feature) need no migration. These are the single source of truth — every
// consumer that must skip paused meds filters through them.

const normMedName = (s: unknown): string => String(s ?? "").trim().toLowerCase();

/** Absent active flag ⇒ active. Inactive only when explicitly `false`. */
export const isMedItemActive = (it: { active?: boolean }): boolean => it.active !== false;

/**
 * Split items into active and inactive, preserving original order within each
 * group. Callers render `[...active, ...inactive]` to float paused meds to the
 * end of a list/chip row (D-G).
 */
export function partitionActiveItems<T extends { active?: boolean }>(
  items: T[]
): { active: T[]; inactive: T[] } {
  const active: T[] = [];
  const inactive: T[] = [];
  for (const it of items) (isMedItemActive(it) ? active : inactive).push(it);
  return { active, inactive };
}

/** Distinct, normalized (lowercased, trimmed) names of all inactive items across regimens. */
export function inactiveMedNames(regimens: Regimen[]): Set<string> {
  const out = new Set<string>();
  for (const r of regimens) {
    for (const it of r.items ?? []) {
      if (!isMedItemActive(it)) {
        const nm = normMedName(it.name);
        if (nm) out.add(nm);
      }
    }
  }
  return out;
}

/**
 * Group-capture tool: the model calls this when the user reports taking a saved
 * scheduled regimen ("I took my morning pills"), optionally excluding some meds.
 * The server expands it into one medication_taken event per kept item (§17.11).
 */
export const RECORD_REGIMEN_TOOL = {
  name: "record_regimen_taken",
  description:
    "Record that the user took the medications in a SAVED scheduled regimen (e.g. 'morning pills', " +
    "'night pills'). Use this instead of recording each medication separately when the user refers " +
    "to a regimen as a group. Pass any medications the user says they did NOT take in 'exclusions'.",
  input_schema: {
    type: "object",
    properties: {
      regimenName: {
        type: "string",
        description: "Name of the saved regimen exactly as listed in the patient's regimens.",
      },
      takenAt: {
        type: "string",
        description:
          "ISO-8601 with UTC offset. Omit if the user gave no time cue — the system will record " +
          "the current time automatically.",
      },
      exclusions: {
        type: "array",
        items: { type: "string" },
        description: "Names of medications in the regimen that were NOT taken now.",
      },
    },
    required: ["regimenName"],
  },
} as const;
